import { createIsomorphicFn } from "@tanstack/react-start";
import { createContext, type ReactNode, use, useEffect, useState } from "react";
import { z } from "zod";
import { useConfiguration } from "./ConfigProvider";

const UserThemeSchema = z.enum(["light", "dark", "system"]).catch("system");
const AppThemeSchema = z.enum(["light", "dark"]).catch("light");

export type UserTheme = z.infer<typeof UserThemeSchema>;
export type AppTheme = z.infer<typeof AppThemeSchema>;

const themeStorageKey = "ui-theme";

const getStoredUserTheme = createIsomorphicFn()
	.server((): UserTheme => "system")
	.client((): UserTheme => {
		const stored = localStorage.getItem(themeStorageKey);
		return UserThemeSchema.parse(stored);
	});

const setStoredTheme = (theme: UserTheme) => {
	const validatedTheme = UserThemeSchema.parse(theme);
	localStorage.setItem(themeStorageKey, validatedTheme);
};

const getSystemTheme = createIsomorphicFn()
	.server((): AppTheme => "light")
	.client((): AppTheme => {
		// If dark mode is not allowed, always return light
		const config = useConfiguration();
		if (!config?.flags?.allowDarkMode) {
			return "light";
		}
		// Dark mode is allowed, check system preference
		return window.matchMedia("(prefers-color-scheme: dark)").matches
			? "dark"
			: "light";
	});

const handleThemeChange = (userTheme: UserTheme) => {
	const validatedTheme = UserThemeSchema.parse(userTheme);

	const root = document.documentElement;
	root.classList.remove("light", "dark", "system");

	if (validatedTheme === "system") {
		const systemTheme = getSystemTheme();
		root.classList.add(systemTheme, "system");
	} else {
		root.classList.add(validatedTheme);
	}
};

const setupPreferredListener = () => {
	const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
	const handler = () => {
		// Re-apply system theme when system preference changes
		const currentUserTheme = UserThemeSchema.parse(
			localStorage.getItem(themeStorageKey),
		);
		if (currentUserTheme === "system") {
			handleThemeChange("system");
		}
	};
	mediaQuery.addEventListener("change", handler);
	return () => mediaQuery.removeEventListener("change", handler);
};

type ThemeContextProps = {
	userTheme: UserTheme;
	appTheme: AppTheme;
	setTheme: (theme: UserTheme) => void;
};
const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

type ThemeProviderProps = {
	children: ReactNode;
};
export function ThemeProvider({ children }: ThemeProviderProps) {
	const [userTheme, setUserTheme] = useState<UserTheme>(getStoredUserTheme);

	const config = useConfiguration();
	const allowDarkMode = config?.flags?.allowDarkMode;

	if (!allowDarkMode) {
		const forceLightMode = "light";

		const setTheme = (_theme: UserTheme) => {
			// Always force light mode when dark mode is disabled
			setUserTheme(forceLightMode);
			setStoredTheme?.(forceLightMode);
			handleThemeChange?.(forceLightMode);
		};

		// Use effect to set localStorage on client side only
		// biome-ignore lint/correctness/useExhaustiveDependencies: <false positive>
		// biome-ignore lint/correctness/useHookAtTopLevel: <false positive>
		useEffect(() => {
			setStoredTheme?.(forceLightMode);
		}, [forceLightMode]);

		return (
			<ThemeContext
				value={{
					userTheme: forceLightMode,
					appTheme: forceLightMode,
					setTheme,
				}}
			>
				{children}
			</ThemeContext>
		);
	}

	// Initialize theme on mount (theme classes should already be applied by blocking script)
	// biome-ignore lint/correctness/useHookAtTopLevel: <false positive>
	useEffect(() => {
		handleThemeChange(userTheme);
	}, [userTheme]);

	// biome-ignore lint/correctness/useHookAtTopLevel: <false positive>
	useEffect(() => {
		if (userTheme !== "system") return;
		return setupPreferredListener();
	}, [userTheme]);

	const appTheme = userTheme === "system" ? getSystemTheme() : userTheme;

	const setTheme = (newUserTheme: UserTheme) => {
		const validatedTheme = UserThemeSchema.parse(newUserTheme);
		setUserTheme(validatedTheme);
		setStoredTheme(validatedTheme);
		handleThemeChange(validatedTheme);
	};

	return (
		<ThemeContext value={{ userTheme, appTheme, setTheme }}>
			{children}
		</ThemeContext>
	);
}

export const useTheme = () => {
	const context = use(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
};
